<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, type Component } from 'vue';
import { useFocusTrap, useScrollLock } from '../feedback/useOverlayA11y';
import { LUMI_MODULES, type LumiModuleKey } from '../modules';
import { MODULE_ART } from './moduleArt';
import LumiIcon from '../icons/LumiIcon.vue';

/** A module the user can switch to — supplied by the consumer app. */
export interface ModuleSwitchTarget {
  /** Module key — picks the glyph + accent from the registry. */
  key: LumiModuleKey;
  /** The module's localized name. Required: the registry carries no displayed
   * string, so the consumer resolves it (app-kit's `lumiModules.<key>.name`). */
  label: string;
  /** One-line localized purpose shown under the name in the launcher grid. */
  description?: string;
  /** Overrides the registry glyph on this target's launcher tile (Home wears the
   * house, not the four-squares `module.suite` glyph). A module with a clay piece
   * (MODULE_ART) wears the piece instead. */
  icon?: Component;
  /** Absolute URL of the target module (the consumer resolves the peer URL). */
  href: string;
}

/**
 * A sellable module the workspace does NOT have (issue 261) — shown so people can
 * discover that the suite is bigger than their workspace.
 *
 * A DELIBERATELY DIFFERENT TYPE from ModuleSwitchTarget, not a flag on one. The
 * switcher is fail-closed on purpose (the app-kit builder buildModuleSwitchTargets
 * lists nothing when module access is unconfirmed), and the worst thing this
 * feature could do is turn an unconfirmed access list into a wall of upsells. A
 * shared type with an `isTeaser` boolean is one careless refactor away from
 * exactly that, so a teaser has NO `href` field at all and cannot be handed to
 * the tile loop by mistake.
 *
 * `billingHref` is the ONE difference between the two audiences, and it is the
 * consumer's capability decision (canManageBilling — owner, billing_manager or
 * suite_admin), never re-derived here: present means "you may change the
 * subscription, here is the way"; absent means the teaser carries no control at
 * all, and the consumer says which role to ask via `teaserNote` instead.
 */
export interface ModuleTeaser {
  key: LumiModuleKey;
  /** The module's localized name (required — see ModuleSwitchTarget). */
  label: string;
  /** One-line localized purpose. */
  description?: string;
  /** Buyers only — the workspace billing page's add section. */
  billingHref?: string;
}

const props = withDefaults(
  defineProps<{
    module?: LumiModuleKey;
    /**
     * The current module's localized name, shown in the pill. Falls back to the
     * label of the matching entry in `modules` (the switcher always lists the
     * current module); with neither the pill shows no name rather than a guess.
     */
    label?: string;
    /** Overrides the registry glyph for the pill only (the workspace pill wears the
     * house, not the four-squares `module.suite` glyph). A module with a clay piece
     * (MODULE_ART) wears the piece instead. */
    icon?: Component;
    /**
     * `default` — the switcher pill (wash ground, panel-edge ring, capsule
     * padding): the sidebar/launcher look, unchanged.
     * `plain` — a bare espresso icon square + bold name, no wash fill, no ring,
     * no capsule padding (the workspace home's owned-module card header, whose
     * arrow and hover-shift already live in the CONSUMER's wrapper button — this
     * variant only strips the pill chrome around the icon+name pair).
     */
    variant?: 'default' | 'plain';
    /**
     * When provided (non-empty) the badge becomes an interactive switcher that
     * opens the app-launcher overlay for the supplied modules. Empty → plain
     * static badge.
     */
    modules?: ModuleSwitchTarget[];
    /** Also doubles as the launcher overlay's heading / aria-label. */
    switcherLabel?: string;
    /**
     * Label over the modules below Home (#1039), e.g. "Your modules". Home is the
     * start page across every module, not one of them, so it stands first and
     * apart. No English default, same reasoning as teaserHeading: without it the
     * modules still form their own group, just unlabelled.
     */
    moduleListHeading?: string;
    closeLabel?: string;
    /**
     * Set when the app could not read the user's module access. The list is
     * fail-closed in that case, so it is short or empty for a reason that has
     * nothing to do with entitlement — the note says so inside the overlay rather
     * than letting a missing module read as "you do not have it" (issue 195).
     * The consumer supplies the localized sentence.
     */
    accessNote?: string;
    /**
     * Sellable modules the workspace does not have (issue 261). Rendered as their
     * own labelled group BELOW the switcher grid, never mixed into it: the tiles
     * above are places you can go, these are not, and the brief's bar is that a
     * glancing user tells the two apart without reading anything.
     *
     * Fail-closed by construction — the consumer passes an empty array whenever
     * module access is unconfirmed, so an unknown access map yields no tiles AND
     * no teasers.
     */
    teasers?: ModuleTeaser[];
    /** Group heading, e.g. "Also part of Lumi". No English default: the group is
     * not rendered without it, so a missing translation degrades to today's
     * launcher rather than shipping one language to every locale. */
    teaserHeading?: string;
    /** The visible marker that makes a teaser a different KIND of thing, e.g.
     * "Not in your plan". It is also what a screen reader announces instead of a
     * broken link, which is why it is real text and not an SR-only string. Same
     * no-default rule as teaserHeading — both are required to render the group. */
    teaserTagLabel?: string;
    /** Buyers only — label of the link into the billing add flow. */
    teaserActionLabel?: string;
    /** Non-buyers only — which ROLE can add a module (never which person). */
    teaserNote?: string;
  }>(),
  {
    module: 'suite',
    label: '',
    icon: undefined,
    variant: 'default',
    modules: () => [],
    switcherLabel: 'Switch module',
    moduleListHeading: '',
    closeLabel: 'Close',
    accessNote: '',
    teasers: () => [],
    teaserHeading: '',
    teaserTagLabel: '',
    teaserActionLabel: '',
    teaserNote: '',
  },
);

const mod = computed(() => LUMI_MODULES[props.module] ?? LUMI_MODULES.suite);
const pillIcon = computed(() => props.icon ?? mod.value.icon);
const pillArt = computed(() => MODULE_ART[mod.value.key]);
const pillName = computed(
  () => props.label || props.modules.find((t) => t.key === props.module)?.label || '',
);
// Both labels are required to render the group at all — see the prop docs. A
// teaser without its "not in your plan" tag would be indistinguishable from an
// owned module that has lost its link, which is the one reading this feature
// must never produce.
const showTeasers = computed(
  () => (props.teasers?.length ?? 0) > 0 && !!props.teaserHeading && !!props.teaserTagLabel,
);
function teaserModule(t: ModuleTeaser) {
  return LUMI_MODULES[t.key] ?? LUMI_MODULES.suite;
}

// A note with nowhere to open would be no better than the silence it replaces, so
// an unconfirmed access list makes the badge interactive even when it is empty.
// Teasers count as content: a workspace that owns nothing but Core would otherwise
// have a launcher worth opening and no way to open it. Safe against the fail-closed
// rule because the consumer only ever supplies teasers for a CONFIRMED access map.
const interactive = computed(
  () => (props.modules?.length ?? 0) > 0 || !!props.accessNote || showTeasers.value,
);

const open = ref(false);
const panelRef = ref<HTMLElement | null>(null);
function toggle() {
  if (interactive.value) open.value = !open.value;
}
function close() {
  open.value = false;
}
// Home (the workspace, key `suite`) leads the launcher on its own; the booked
// modules follow as a group (#1039).
const homeTargets = computed(() => props.modules.filter((t) => t.key === 'suite'));
const moduleTargets = computed(() => props.modules.filter((t) => t.key !== 'suite'));
function targetModule(t: ModuleSwitchTarget) {
  return LUMI_MODULES[t.key] ?? LUMI_MODULES.suite;
}
// Shared overlay a11y mechanics (nested-safe scroll lock + focus trap/return) —
// the same primitives Modal/Drawer/Workbench use. This is a teleported overlay
// now, not an anchored in-flow menu, so it needs the same treatment as those.
useScrollLock(open);
const { isTopmost } = useFocusTrap(open, panelRef);

function onBackdrop(e: MouseEvent) {
  if (e.target === e.currentTarget) close();
}
// Self-contained Escape-to-close, scoped to the topmost overlay (Modal.vue's
// established pattern) so a dialog opened from inside the launcher would own
// Escape over this one rather than both racing for it.
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value && isTopmost()) close();
}
watch(open, (isOpen) => {
  if (typeof document === 'undefined') return;
  if (isOpen) document.addEventListener('keydown', onKey);
  else document.removeEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  if (typeof document !== 'undefined') document.removeEventListener('keydown', onKey);
});

// Global keyboard shortcut (issue 195: "reachable from the badge and a keyboard
// shortcut"): Ctrl/Cmd+Shift+M, live whenever this badge is interactive — not
// gated on focus being anywhere near the badge, so it works from wherever the
// user is in the app. Deliberately NOT Ctrl/Cmd+K:
// apprentice-ui's own CommandPalette already owns that binding app-wide.
const isMac =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
const modKeyLabel = isMac ? '⌘' : 'Ctrl';
function onShortcut(e: KeyboardEvent) {
  if (!interactive.value) return;
  const modKey = e.metaKey || e.ctrlKey;
  if (modKey && e.shiftKey && (e.key === 'm' || e.key === 'M')) {
    e.preventDefault();
    open.value = !open.value;
  }
}
onMounted(() => {
  if (typeof window !== 'undefined') window.addEventListener('keydown', onShortcut);
});
onBeforeUnmount(() => {
  if (typeof window !== 'undefined') window.removeEventListener('keydown', onShortcut);
});
</script>

<template>
  <!-- The module switcher (spec §4): a wash pill with the panel-edge ring, the
       module's glyph on an espresso square, its name, and — when it switches —
       a chevron. No colour dot: the palette no longer varies by module. -->

  <!-- Static badge — the same pill, nothing to open. -->
  <span
    v-if="!interactive"
    class="lumi-modbadge"
    :class="{ 'lumi-modbadge--plain': variant === 'plain' }"
    :data-module="mod.key"
  >
    <span v-if="pillArt" class="lumi-modbadge__icon lumi-modbadge__icon--art"><img :src="pillArt" alt="" /></span>
    <span v-else class="lumi-modbadge__icon"><component :is="pillIcon" aria-hidden="true" /></span>
    <span class="lumi-modbadge__name"><slot>{{ pillName }}</slot></span>
    <!-- Degraded-access note, visible without opening anything: an overlay-only
         note is easy to never see on a switcher that looks perfectly normal
         closed (issue 195). -->
    <span v-if="accessNote" class="lumi-modbadge__flag" role="status" :title="accessNote">
      <span class="lumi-modbadge__flag-sr">{{ accessNote }}</span>
    </span>
  </span>

  <!-- Interactive badge — opens the app-launcher overlay (Teleport, below). -->
  <button
    v-else
    type="button"
    class="lumi-modbadge lumi-modbadge--button"
    :class="{ 'lumi-modbadge--plain': variant === 'plain' }"
    :data-module="mod.key"
    :aria-haspopup="'dialog'"
    :aria-expanded="open"
    :aria-label="switcherLabel"
    @click="toggle"
  >
    <span v-if="pillArt" class="lumi-modbadge__icon lumi-modbadge__icon--art"><img :src="pillArt" alt="" /></span>
    <span v-else class="lumi-modbadge__icon"><component :is="pillIcon" aria-hidden="true" /></span>
    <span class="lumi-modbadge__name"><slot>{{ pillName }}</slot></span>
    <!-- Same note, visible without opening the overlay (issue 195). -->
    <span v-if="accessNote" class="lumi-modbadge__flag" role="status" :title="accessNote">
      <span class="lumi-modbadge__flag-sr">{{ accessNote }}</span>
    </span>
    <LumiIcon name="expand" class="lumi-modbadge__caret" :class="{ 'lumi-modbadge__caret--open': open }" width="14" height="14" stroke-width="2.5" />
  </button>

  <!-- App-launcher overlay: a labelled grid (icon, name, one-line purpose),
       workspace first — the order `buildModuleSwitchTargets` already returns
       (issue 195). Teleported so it sits above app chrome regardless of where
       the badge lives, and reachable via the keyboard shortcut from anywhere
       in the app. -->
  <Teleport to="body">
    <div v-if="open" class="lumi-launcher-backdrop" @mousedown="onBackdrop">
      <div
        ref="panelRef"
        class="lumi-launcher"
        role="dialog"
        aria-modal="true"
        :aria-label="switcherLabel"
      >
        <div class="lumi-launcher__header">
          <h2 class="lumi-launcher__title">{{ switcherLabel }}</h2>
          <button type="button" class="lumi-launcher__close" :aria-label="closeLabel" @click="close">
            <LumiIcon name="remove" width="18" height="18" stroke-width="2.5" />
          </button>
        </div>

        <!-- One column, one equal row per target (#1039): a grid gave an odd
             trailing tile a whole row to itself, wider and shorter than its
             neighbours. Home comes first and looks like every module, set apart
             only by the rule and label above the modules that follow it. -->
        <div class="lumi-launcher__grid" role="menu" :aria-label="switcherLabel">
          <template v-for="(group, gi) in [homeTargets, moduleTargets]" :key="gi">
            <div
              v-if="group.length"
              class="lumi-launcher__group"
              :class="{ 'lumi-launcher__group--modules': gi === 1 && homeTargets.length }"
              role="group"
              :aria-label="gi === 1 && moduleListHeading ? moduleListHeading : undefined"
            >
              <span
                v-if="gi === 1 && homeTargets.length && moduleListHeading"
                class="lumi-launcher__group-title"
                aria-hidden="true"
              >{{ moduleListHeading }}</span>
              <a
                v-for="target in group"
                :key="target.key + target.href"
                :href="target.key === module ? undefined : target.href"
                class="lumi-launcher__tile"
                :class="{ 'lumi-launcher__tile--current': target.key === module }"
                :data-module="target.key"
                role="menuitem"
                :aria-current="target.key === module ? 'true' : undefined"
                @click="close"
              >
                <span v-if="MODULE_ART[target.key]" class="lumi-launcher__icon lumi-launcher__icon--art">
                  <img :src="MODULE_ART[target.key]" alt="" />
                </span>
                <span v-else class="lumi-launcher__icon">
                  <component :is="target.icon ?? targetModule(target).icon" width="22" height="22" aria-hidden="true" />
                </span>
                <span class="lumi-launcher__text">
                  <span class="lumi-launcher__name">
                    {{ target.label }}
                    <LumiIcon v-if="target.key === module" name="confirm" class="lumi-launcher__check" width="14" height="14" stroke-width="2.5" />
                  </span>
                  <span class="lumi-launcher__desc">{{ target.description }}</span>
                </span>
              </a>
            </div>
          </template>
        </div>

        <!-- The offering the workspace does NOT have (issue 261). Its own labelled
             group below the grid rather than ghosted tiles inside it: the brief's
             bar is that someone scanning for a second never mistakes a teaser for
             something they own and clicks into it. So these are <li>, never <a> —
             not focusable, not activatable, no href to derive a URL from — and
             each carries a visible "not in your plan" tag that doubles as what a
             screen reader announces. The only focusable thing in here is the
             buyer's billing link, which goes somewhere real. -->
        <div v-if="showTeasers" class="lumi-launcher__teasers">
          <h3 class="lumi-launcher__teasers-title">{{ teaserHeading }}</h3>
          <ul class="lumi-launcher__teaser-grid" role="list">
            <li
              v-for="teaser in teasers"
              :key="teaser.key"
              class="lumi-launcher__teaser"
              :data-module="teaser.key"
            >
              <span class="lumi-launcher__teaser-icon">
                <component :is="teaserModule(teaser).icon" width="20" height="20" aria-hidden="true" />
              </span>
              <span class="lumi-launcher__text">
                <span class="lumi-launcher__teaser-name">
                  {{ teaser.label }}
                  <span class="lumi-launcher__teaser-tag">{{ teaserTagLabel }}</span>
                </span>
                <span class="lumi-launcher__desc">{{ teaser.description }}</span>
                <a
                  v-if="teaser.billingHref && teaserActionLabel"
                  class="lumi-launcher__teaser-cta"
                  :href="teaser.billingHref"
                  @click="close"
                  >{{ teaserActionLabel }}</a
                >
              </span>
            </li>
          </ul>
          <!-- Non-buyers: which ROLE can add a module, never which person -- naming
               an individual would expose more about the workspace's role
               assignments than the app otherwise does. -->
          <p v-if="teaserNote" class="lumi-launcher__teaser-note">{{ teaserNote }}</p>
        </div>

        <p v-if="accessNote" class="lumi-launcher__note" role="note">{{ accessNote }}</p>

        <p class="lumi-launcher__hint">{{ modKeyLabel }}+Shift+M</p>
      </div>
    </div>
  </Teleport>
</template>

<style>
/* The pill: full width of the nav column, wash ground, panel-edge ring (spec §4). */
.lumi-modbadge{position:relative;display:flex;align-items:center;gap:10px;width:100%;padding:6px 10px 6px 6px;border-radius:var(--radius-lg);background:var(--surface-wash);border:1px solid var(--pill-ring);color:var(--text-heading);font-family:var(--font-body);font-size:var(--text-sm);font-weight:var(--weight-semibold);white-space:nowrap;text-align:left;}
.lumi-modbadge__icon{display:flex;align-items:center;justify-content:center;flex-shrink:0;width:28px;height:28px;border-radius:8px;background:var(--btn-primary-bg);color:var(--btn-primary-fg);}
.lumi-modbadge__icon svg{width:16px;height:16px;flex-shrink:0;}
/* A clay piece (issue 835) sits on a pale wash of its module colour, never on the full
   colour: the pieces carry those colours themselves and drown on them. */
.lumi-modbadge[data-module='people']{--nav-module:var(--nav-module-people);}
.lumi-modbadge[data-module='ats']{--nav-module:var(--nav-module-ats);}
.lumi-modbadge[data-module='time']{--nav-module:var(--nav-module-time);}
.lumi-modbadge[data-module='apprentice']{--nav-module:var(--nav-module-apprentice);}
.lumi-modbadge__icon--art{background:color-mix(in oklab,var(--nav-module) 20%,var(--surface-card));}
.lumi-modbadge__icon--art img{display:block;width:24px;height:24px;flex-shrink:0;}
.lumi-modbadge__name{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;}
/* Quiet, always-visible degraded-access signal (issue 195): a small dot in the
   badge's corner rather than a second line of text — this is a degraded nav, not
   an error page, and it must not compete with the module name for attention. The
   note itself is a native `title` tooltip plus screen-reader text, not more chrome. */
.lumi-modbadge__flag{position:absolute;top:-3px;right:-3px;width:8px;height:8px;border-radius:50%;background:var(--warning);border:1.5px solid var(--shell-canvas);}
.lumi-modbadge__flag-sr{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}
.lumi-modbadge--button{cursor:pointer;transition:background-color var(--dur-fast),border-color var(--dur-fast);}
.lumi-modbadge--button:hover{background:var(--accent-soft-2);border-color:var(--panel-edge);}
/* `plain` (owned-module card header): the bare icon square + bold name, no wash
   fill, no ring, no capsule padding — the arrow and its hover-shift belong to the
   consumer's own wrapper button (HomebaseModuleCard), so this variant strips only
   the pill chrome around the icon+name pair. */
.lumi-modbadge--plain{background:none;border-color:transparent;padding:0;width:auto;}
.lumi-modbadge--plain .lumi-modbadge__name{font-weight:var(--weight-bold);}
.lumi-modbadge--button.lumi-modbadge--plain:hover{background:none;border-color:transparent;}
.lumi-modbadge--button.lumi-modbadge--plain:hover .lumi-modbadge__name{text-decoration:underline;}
.lumi-modbadge__caret{flex-shrink:0;color:var(--text-secondary);transition:transform var(--dur-fast) var(--ease-out);}
.lumi-modbadge__caret--open{transform:rotate(180deg);}

/* App-launcher overlay: full-screen backdrop + centered dialog, same layer as
   Modal.vue's primary content (--layer-overlay-modal) since it is a peer of the
   same overlay family, never anchored/in-flow like the old dropdown. */
.lumi-launcher-backdrop{position:fixed;inset:0;z-index:var(--layer-overlay-modal);background:var(--overlay);backdrop-filter:blur(2px);display:flex;align-items:flex-start;justify-content:center;padding:10vh var(--space-4) var(--space-4);overflow-y:auto;}
.lumi-launcher{width:100%;max-width:520px;background:var(--surface-card);border-radius:var(--radius-xl);box-shadow:var(--shadow-modal);padding:var(--space-6);}
.lumi-launcher__header{display:flex;align-items:center;justify-content:space-between;gap:var(--space-4);margin-bottom:var(--space-4);}
.lumi-launcher__title{margin:0;font-family:var(--font-display);font-size:var(--text-lg);font-weight:var(--weight-semibold);color:var(--text-heading);}
.lumi-launcher__close{display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;border:none;border-radius:var(--radius-md);background:transparent;color:var(--text-muted);cursor:pointer;transition:background-color var(--dur-fast);}
.lumi-launcher__close:hover{background:var(--surface-wash);}

/* One column (#1039): every target the same width and height whatever the count.
   Home's group stands first; the modules' group opens with a rule and its label. */
.lumi-launcher__grid,.lumi-launcher__group{display:flex;flex-direction:column;gap:var(--space-2);}
.lumi-launcher__grid{gap:0;}
.lumi-launcher__group--modules{margin-top:var(--space-4);padding-top:var(--space-3);border-top:1px solid var(--border-default);}
.lumi-launcher__group-title{margin:0 0 var(--space-1);font-family:var(--font-body);font-size:var(--text-xs);font-weight:var(--weight-semibold);letter-spacing:var(--tracking-caps);text-transform:uppercase;color:var(--text-muted);}
.lumi-launcher__tile{display:flex;align-items:center;gap:var(--space-3);padding:var(--space-3);border-radius:var(--radius-lg);border:1px solid var(--border-default);color:var(--text-body);text-decoration:none;cursor:pointer;transition:background-color var(--dur-fast),border-color var(--dur-fast);}
.lumi-launcher__tile:hover{background:var(--surface-wash);text-decoration:none;}
.lumi-launcher__tile--current{background:var(--accent-soft);border-color:var(--accent);}
.lumi-launcher__tile--current:hover{background:var(--accent-soft-2);}
.lumi-launcher__icon{display:flex;align-items:center;justify-content:center;flex-shrink:0;width:40px;height:40px;border-radius:var(--radius-icon);background:var(--btn-primary-bg);color:var(--btn-primary-fg);}
/* The Heroicons glyph is an SVG flex item inside a 40px flex well. WebKit sizes
   such an item from its content box, and an SVG whose only size comes from its
   width/height ATTRIBUTES can collapse to 0 there (issue 404) -- the sidebar one file
   over already pins its icons the same way. Explicit CSS sizing + no shrink. */
.lumi-launcher__icon svg{width:22px;height:22px;flex-shrink:0;}
/* Each tile wears its module's colour (tokens: --nav-module-*), Home its lavender —
   the switcher and the sidebar are the only places these colours appear. */
.lumi-launcher__tile[data-module='people']{--nav-module:var(--nav-module-people);}
.lumi-launcher__tile[data-module='ats']{--nav-module:var(--nav-module-ats);}
.lumi-launcher__tile[data-module='time']{--nav-module:var(--nav-module-time);}
.lumi-launcher__tile[data-module='apprentice']{--nav-module:var(--nav-module-apprentice);}
.lumi-launcher__tile[data-module='suite']{--nav-module:var(--nav-highlight);}
.lumi-launcher__tile:is([data-module='people'],[data-module='ats'],[data-module='time'],[data-module='apprentice']) .lumi-launcher__icon{background:var(--nav-module);color:var(--text-heading);}
.lumi-launcher__tile[data-module='suite'] .lumi-launcher__icon{background:var(--nav-highlight);color:var(--text-inverse);}
/* A clay piece keeps the sidebar badge's pale wash (#1114) — it has to outrank the
   two :is() rules above, which would otherwise paint the full module colour. */
.lumi-launcher__grid .lumi-launcher__tile .lumi-launcher__icon.lumi-launcher__icon--art{background:color-mix(in oklab,var(--nav-module) 20%,var(--surface-card));}
.lumi-launcher__icon--art img{display:block;width:34px;height:34px;flex-shrink:0;}
.lumi-launcher__tile:is([data-module='people'],[data-module='ats'],[data-module='time'],[data-module='apprentice'],[data-module='suite']):hover{background:color-mix(in oklab,var(--nav-module) 8%,var(--surface-card));}
.lumi-launcher__tile--current:is([data-module='people'],[data-module='ats'],[data-module='time'],[data-module='apprentice'],[data-module='suite']),.lumi-launcher__tile--current:is([data-module='people'],[data-module='ats'],[data-module='time'],[data-module='apprentice'],[data-module='suite']):hover{background:color-mix(in oklab,var(--nav-module) 14%,var(--surface-card));border-color:color-mix(in oklab,var(--nav-module) 50%,var(--text-heading));}
.lumi-launcher__text{min-width:0;display:flex;flex-direction:column;gap:2px;}
.lumi-launcher__name{display:flex;align-items:center;gap:6px;font-family:var(--font-body);font-size:var(--text-sm);font-weight:var(--weight-semibold);color:var(--text-heading);}
.lumi-launcher__check{flex-shrink:0;color:var(--accent-text);}
.lumi-launcher__desc{font-family:var(--font-body);font-size:var(--text-xs);color:var(--text-muted);line-height:1.4;}

/* Teasers: a different KIND of thing, not the same tile at a lower opacity
   (issue 261's quality bar). Three signals do that work at a glance -- no card
   surface, a DASHED border, and a hollow icon well rather than the filled accent
   chip an owned tile carries -- plus no hover and no pointer, because there is
   nothing to click. Ghosting alone was the explicitly rejected option: it reads
   as "disabled", which invites a click, and it fails in high contrast. */
.lumi-launcher__teasers{margin:var(--space-5) 0 0;padding-top:var(--space-4);border-top:1px solid var(--border-default);}
.lumi-launcher__teasers-title{margin:0 0 var(--space-3);font-family:var(--font-body);font-size:var(--text-xs);font-weight:var(--weight-semibold);letter-spacing:var(--tracking-caps);text-transform:uppercase;color:var(--text-muted);}
.lumi-launcher__teaser-grid{display:flex;flex-direction:column;gap:var(--space-2);margin:0;padding:0;list-style:none;}
.lumi-launcher__teaser{display:flex;align-items:flex-start;gap:var(--space-3);padding:var(--space-3);border-radius:var(--radius-lg);border:1px dashed var(--border-default);background:none;color:var(--text-muted);}
.lumi-launcher__teaser-icon{display:flex;align-items:center;justify-content:center;flex-shrink:0;width:40px;height:40px;border-radius:var(--radius-icon);border:1px dashed var(--border-default);color:var(--text-muted);}
.lumi-launcher__teaser-icon svg{width:20px;height:20px;flex-shrink:0;}
.lumi-launcher__teaser-name{display:flex;align-items:center;flex-wrap:wrap;gap:6px;font-family:var(--font-body);font-size:var(--text-sm);font-weight:var(--weight-semibold);color:var(--text-body);}
.lumi-launcher__teaser-tag{display:inline-block;padding:1px 6px;border-radius:var(--radius-pill);background:var(--surface-wash);color:var(--text-muted);font-size:10px;font-weight:var(--weight-semibold);letter-spacing:var(--tracking-caps);text-transform:uppercase;white-space:nowrap;}
.lumi-launcher__teaser-cta{margin-top:6px;align-self:flex-start;font-family:var(--font-body);font-size:var(--text-xs);font-weight:var(--weight-semibold);color:var(--accent-text);text-decoration:underline;}
.lumi-launcher__teaser-note{margin:var(--space-3) 0 0;color:var(--text-muted);font-family:var(--font-body);font-size:var(--text-xs);line-height:1.4;}

.lumi-launcher__note{margin:var(--space-4) 0 0;padding-top:var(--space-3);border-top:1px solid var(--border-default);color:var(--text-muted);font-family:var(--font-body);font-size:var(--text-xs);line-height:1.4;}
.lumi-launcher__hint{margin:var(--space-2) 0 0;color:var(--text-muted);font-family:var(--font-body);font-size:var(--text-xs);text-align:right;}

</style>
